import type { SyntheticEvent } from 'react'
import { useEffect, useCallback, useState } from 'react'
import { useIntl } from 'umi'
import Message from '@/components/Message'

type ICopyState = 'default' | 'copied' | 'failed'

const useCopyToClipBoard = (text?: string) => {
  const [paper, setPaper] = useState(() => text || '')
  const [copyState, setCopyState] = useState<ICopyState>('default')
  const intl = useIntl()

  const copy2ClipBoard = useCallback(
    (copy: string = paper) => {
      return new Promise<ICopyState>((resolve, reject) => {
        if (!copy) return reject(new Error('no text input!'))
        return window.navigator.clipboard
          .writeText(copy)
          .then(() => {
            setCopyState('copied')
            resolve('copied')
          })
          .catch(() => {
            setCopyState('failed')
            reject(new Error('failed'))
          })
          .finally(() => {
            setTimeout(() => setCopyState('default'))
          })
      })
    },
    [paper, setCopyState],
  )

  const copyText = useCallback(
    (str?: string) => () => {
      if (!str) return
      copy2ClipBoard(str)
        .then(() => {
          Message.success(intl.messages['Global.action.copy.success'])
        })
        .catch(() => {
          Message.error(intl.messages['Global.action.copy.failed'])
        })
    },
    [copy2ClipBoard],
  )

  useEffect(() => {
    if (text) {
      setPaper(text)
    }
  }, [text])

  useEffect(() => {
    if (paper) {
      copyText(paper)
    }
  }, [paper])

  return {
    copy2ClipBoard,
    copyText,
    copyState,
  }
}

export default useCopyToClipBoard
